import React, { useState, useEffect, type ComponentProps } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Table,
  Tag,
  Button,
  Collapse,
  Select,
  Input,
  Empty,
  Grid,
  Pagination,
  Form,
  Typography,
  Space,
  Row,
  Col,
  Alert,
  DatePicker,
  Modal,
  QRCode,
  Spin,
  Tooltip,
} from 'antd'
import {
  SearchOutlined,
  ClearOutlined,
  CopyOutlined,
  QuestionCircleOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import type { MatriculaListItem, Curso } from '@/types'
import { useAlunosStore } from '@/stores/alunosStore'
import { getAllCursos } from '@/api/cursos'
import {
  formatDate,
  formatDateTime,
  formatCpf,
  formatCurrency,
  copyToClipboard,
  stripCpf,
} from '@/lib/formatters'
import { getCobrancaAgregada } from '@/api/parceiros'
import type { AndamentoDoCurso, StatusPagamento } from '@/api/parceiros'
import * as parceirosApi from '@/api/parceiros'
import { useRotuloDeFilho } from '@/lib/rotuloDeFilho'
import { TagDeStatus } from '@/components/TagDeStatus'
import { IniciaisDoAluno } from '@/components/IniciaisDoAluno'
import {
  calculateProgress,
  concluiuOsModulos,
  diasAteConclusaoNoDetran,
  faseDoCurso,
  formatUltimaAtividade,
  ultimaAtividade,
} from '@/lib/progressoDoAluno'

type DateRangeValue = ComponentProps<typeof DatePicker.RangePicker>['value']

const { Title, Text } = Typography

// ─── Entry component: zero logic, reads like wireframe ───────────────────────

export function Alunos() {
  return (
    <div className="pb-28">
      <PageHeader />
      <FiltersPanel />
      <QuickFiltersBar />
      <ListaDeAlunos />
      <AggregatedPixBar />
      <CobrancaAgregadaModal />
      <MatriculasDoAlunoModal />
    </div>
  )
}

// ─── Helper components ────────────────────────────────────────────────────────

function PageHeader() {
  return (
    <div className="mb-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Title level={2} className="!mb-0 !text-[#1a3a5c] !font-bold">
          Alunos Matriculados
        </Title>
        <BuscaAlunoPorCpf />
      </div>
    </div>
  )
}

function BuscaAlunoPorCpf() {
  const navigate = useNavigate()
  const buscando = useAlunosStore((s) => s.buscandoPorCpf)
  const buscarMatriculasPorCpf = useAlunosStore((s) => s.buscarMatriculasPorCpf)
  const carregarCaptadores = useAlunosStore((s) => s.carregarCaptadores)
  const [cpf, setCpf] = useState('')

  useEffect(() => {
    carregarCaptadores()
  }, [])

  async function handleBuscar() {
    const matriculas = await buscarMatriculasPorCpf(cpf)
    if (!matriculas.length) {
      toast.info('Nenhuma matrícula encontrada para esse CPF.')
      return
    }
    if (matriculas.length === 1) navigate(`/detalhes/${matriculas[0].id}`)
  }

  return (
    <div className="flex items-center gap-2">
      <Space.Compact>
        <Input
          value={cpf}
          onChange={(event) => setCpf(event.target.value)}
          onPressEnter={handleBuscar}
          placeholder="Buscar aluno por CPF"
          style={{ width: 240 }}
          allowClear
        />
        <Button
          icon={<SearchOutlined />}
          onClick={handleBuscar}
          loading={buscando}
          disabled={cpf.replace(/\D/g, '').length !== 11}
        >
          Buscar
        </Button>
      </Space.Compact>
      <AjudaBuscaComCaptadores />
    </div>
  )
}

function AjudaBuscaComCaptadores() {
  const rotulo = useRotuloDeFilho()
  const temCaptadores = useAlunosStore((s) => s.captadores.length > 0)

  if (!temCaptadores) return null

  return (
    <Tooltip
      title={`A busca por CPF também alcança os alunos dos seus ${rotulo.plural.toLowerCase()}, que não aparecem na lista.`}
    >
      <QuestionCircleOutlined className="text-gray-400" />
    </Tooltip>
  )
}

function MatriculasDoAlunoModal() {
  const navigate = useNavigate()
  const matriculas = useAlunosStore((s) => s.matriculasDoAluno)
  const limpar = useAlunosStore((s) => s.limparMatriculasDoAluno)

  return (
    <Modal
      open={matriculas.length > 1}
      title={`Matrículas de ${matriculas[0]?.alunoNome ?? 'aluno'}`}
      onCancel={limpar}
      footer={null}
      width={720}
    >
      <Table
        dataSource={matriculas}
        rowKey="id"
        size="small"
        pagination={false}
        columns={[
          { title: 'Curso', dataIndex: 'cursoNome' },
          { title: 'Vendido por', dataIndex: 'parceiroNome' },
          {
            title: 'Criada em',
            dataIndex: 'createdAt',
            render: (createdAt: string) => formatDateTime(createdAt),
          },
          {
            title: 'Pagamento',
            dataIndex: 'pago',
            render: (pago: boolean) => (
              <Tag color={pago ? 'green' : 'orange'}>{pago ? 'Pago' : 'Aguardando'}</Tag>
            ),
          },
          {
            title: '',
            key: 'acoes',
            render: (_, matricula: parceirosApi.MatriculaDoAluno) => (
              <Button
                type="link"
                onClick={() => {
                  limpar()
                  navigate(`/detalhes/${matricula.id}`)
                }}
              >
                Detalhes
              </Button>
            ),
          },
        ]}
      />
    </Modal>
  )
}

function FiltersPanel() {
  const setFilter = useAlunosStore((s) => s.setFilter)
  const resetFilters = useAlunosStore((s) => s.resetFilters)
  const fetchAlunos = useAlunosStore((s) => s.fetchAlunos)
  const andamento = useAlunosStore((s) => s.filters.andamento)
  const pagamento = useAlunosStore((s) => s.filters.pagamento)
  const [cursos, setCursos] = useState<Curso[]>([])

  useEffect(() => {
    getAllCursos()
      .then((todos) => setCursos(todos.filter(jaOfertado)))
      .catch(() => {})
  }, [])

  const formik = useFormik({
    initialValues: {
      nomeAluno: '',
      cpf: '',
      cursoId: '',
      dateRange: null as DateRangeValue,
    },
    onSubmit: async (values) => {
      setFilter('nomeAluno', values.nomeAluno || undefined)
      setFilter('cpf', values.cpf || undefined)
      setFilter('cursoId', values.cursoId || undefined)
      setFilter('createdAtStart', values.dateRange?.[0]?.format('YYYY-MM-DD') || undefined)
      setFilter('createdAtEnd', values.dateRange?.[1]?.format('YYYY-MM-DD') || undefined)
      setFilter('pageNumber', 1)
      await fetchAlunos()
      const { error } = useAlunosStore.getState()
      if (error) toast.error(error)
    },
  })

  async function handleReset() {
    formik.resetForm()
    resetFilters()
    await fetchAlunos()
    const { error } = useAlunosStore.getState()
    if (error) toast.error(error)
  }

  return (
    <Collapse
      className="mb-4 !bg-white [&_.ant-collapse-header]:!bg-white [&_.ant-collapse-header]:!py-4"
      items={[
        {
          key: 'filters',
          label: 'Filtros',
          children: (
            <Form layout="vertical" onFinish={formik.submitForm}>
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item label="Nome">
                    <Input
                      name="nomeAluno"
                      value={formik.values.nomeAluno}
                      onChange={formik.handleChange}
                      placeholder="Nome do aluno"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item label="CPF">
                    <Input
                      name="cpf"
                      value={formik.values.cpf}
                      onChange={formik.handleChange}
                      placeholder="Somente números"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item label="Curso">
                    <Select
                      allowClear
                      showSearch
                      variant="outlined"
                      value={formik.values.cursoId || undefined}
                      onChange={(value) => formik.setFieldValue('cursoId', value ?? '')}
                      placeholder="Selecione"
                      options={cursos.map((c) => ({
                        label: c.nomeReduzido || c.nome,
                        value: String(c.id),
                      }))}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item label="Andamento do Curso">
                    <Select
                      allowClear
                      variant="outlined"
                      value={andamento || undefined}
                      onChange={(value) => setFilter('andamento', value ?? undefined)}
                      placeholder="Selecione"
                      options={OPCOES_DE_ANDAMENTO}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item label="Pagamento">
                    <Select
                      allowClear
                      variant="outlined"
                      value={pagamento || undefined}
                      onChange={(value) => setFilter('pagamento', value ?? undefined)}
                      placeholder="Selecione"
                      options={OPCOES_DE_PAGAMENTO}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item label="Período">
                    <DatePicker.RangePicker
                      value={formik.values.dateRange}
                      onChange={(dates) => formik.setFieldValue('dateRange', dates)}
                      format="DD/MM/YYYY"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                  loading={formik.isSubmitting}
                >
                  Buscar
                </Button>
                <Button onClick={handleReset} icon={<ClearOutlined />}>
                  Limpar
                </Button>
              </Space>
            </Form>
          ),
        },
      ]}
    />
  )
}

const OPCOES_DE_ANDAMENTO: Array<{ label: string; value: AndamentoDoCurso }> = [
  { label: 'Não iniciado', value: 'NAO_INICIADO' },
  { label: 'Em andamento', value: 'EM_ANDAMENTO' },
  { label: 'Aguardando DETRAN', value: 'AGUARDANDO_DETRAN' },
  { label: 'Finalizado', value: 'FINALIZADO' },
]

const OPCOES_DE_PAGAMENTO: Array<{ label: string; value: StatusPagamento }> = [
  { label: 'Pago', value: 'PAGO' },
  { label: 'Pendente', value: 'PENDENTE' },
  { label: 'Vencido', value: 'VENCIDO' },
]

const QUICK_FILTERS: Array<{
  label: string
  andamento?: AndamentoDoCurso
  pagamento?: StatusPagamento
}> = [
  { label: 'Todos' },
  { label: 'Pendente', pagamento: 'PENDENTE' },
  { label: 'Vencidos', pagamento: 'VENCIDO' },
  { label: 'Em andamento', andamento: 'EM_ANDAMENTO' },
  { label: 'Finalizados', andamento: 'FINALIZADO' },
]

function QuickFiltersBar() {
  return (
    <div className="flex flex-wrap items-center justify-between mt-4 mb-4 gap-4">
      <QuickFilters />
      <AlunosSummary />
    </div>
  )
}

function QuickFilters() {
  const andamentoAtivo = useAlunosStore((s) => s.filters.andamento)
  const pagamentoAtivo = useAlunosStore((s) => s.filters.pagamento)
  const setFilter = useAlunosStore((s) => s.setFilter)
  const fetchAlunos = useAlunosStore((s) => s.fetchAlunos)

  async function applyQuickFilter(andamento?: AndamentoDoCurso, pagamento?: StatusPagamento) {
    setFilter('andamento', andamento)
    setFilter('pagamento', pagamento)
    setFilter('pageNumber', 1)
    await fetchAlunos()
    const { error } = useAlunosStore.getState()
    if (error) toast.error(error)
  }

  return (
    <div className="flex gap-3 flex-wrap">
      {QUICK_FILTERS.map(({ label, andamento, pagamento }) => {
        const ativo = andamentoAtivo === andamento && pagamentoAtivo === pagamento
        return (
          <Button
            key={label}
            type={ativo ? 'primary' : 'default'}
            size="large"
            className={ativo ? '!font-semibold' : '!border-gray-200 !text-gray-700'}
            onClick={() => applyQuickFilter(andamento, pagamento)}
          >
            {label}
          </Button>
        )
      })}
    </div>
  )
}

function AlunosSummary() {
  const totalRecords = useAlunosStore((s) => s.totalRecords)

  return (
    <div className="shrink-0 bg-white border border-gray-200 rounded-lg px-5 py-3">
      <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium">
        Matrículas
      </div>
      <div className="text-lg font-bold text-[#1a3a5c]">{totalRecords}</div>
    </div>
  )
}

function ListaDeAlunos() {
  const isMobile = !Grid.useBreakpoint().md
  const data = useAlunosStore((s) => s.data)
  const error = useAlunosStore((s) => s.error)

  useCarregarAlunosAoAbrir()

  if (error && !data.length) {
    return <Alert description={error} type="error" showIcon className="mt-4" />
  }

  return isMobile ? <AlunosCards /> : <StudentsTable />
}

function useCarregarAlunosAoAbrir() {
  const location = useLocation()
  const setFilter = useAlunosStore((s) => s.setFilter)
  const fetchAlunos = useAlunosStore((s) => s.fetchAlunos)

  useEffect(() => {
    const state = location.state as {
      andamento?: AndamentoDoCurso
      pagamento?: StatusPagamento
    } | null
    if (state?.andamento) setFilter('andamento', state.andamento)
    if (state?.pagamento) setFilter('pagamento', state.pagamento)
    fetchAlunos()
  }, [])
}

async function irParaPagina(page: number, pageSize: number) {
  const { setFilter, fetchAlunos } = useAlunosStore.getState()
  setFilter('pageNumber', page)
  setFilter('limit', pageSize)
  await fetchAlunos()
  const { error } = useAlunosStore.getState()
  if (error) toast.error(error)
}

function StudentsTable() {
  const navigate = useNavigate()
  const data = useAlunosStore((s) => s.data)
  const totalRecords = useAlunosStore((s) => s.totalRecords)
  const loading = useAlunosStore((s) => s.loading)
  const filters = useAlunosStore((s) => s.filters)
  const selectedIds = useAlunosStore((s) => s.selectedIds)
  const setSelectedIds = useAlunosStore((s) => s.setSelectedIds)

  const rowSelection = {
    selectedRowKeys: selectedIds,
    onChange: (keys: React.Key[]) => setSelectedIds(keys as number[]),
    getCheckboxProps: (record: MatriculaListItem) => ({
      disabled: record.pagamento.dataPagamento !== null,
    }),
  }

  return (
    <Table
      dataSource={data}
      rowKey="id"
      loading={loading}
      rowSelection={rowSelection}
      scroll={{ x: 'max-content' }}
      className="[&_.ant-table-thead>tr>th]:!bg-[#f9fafb] [&_.ant-table-thead>tr>th]:!text-gray-500 [&_.ant-table-thead>tr>th]:!font-medium"
      locale={{ emptyText: 'Nenhum aluno matriculado ainda' }}
      columns={[
        {
          title: 'Dados do Aluno',
          key: 'aluno',
          render: (_, record: MatriculaListItem) => <AlunoCell record={record} />,
        },
        {
          title: 'Curso',
          key: 'curso',
          render: (_, record: MatriculaListItem) => {
            const { sigle, nomeReduzido, nome } = record.curso
            const label = sigle || nomeReduzido || nome
            return (
              <Text title={nome} className="!text-[#1a3a5c]">
                {label}
              </Text>
            )
          },
        },
        {
          title: 'Pagamento',
          key: 'pagamento',
          render: (_, record: MatriculaListItem) => <PagamentoCell record={record} />,
        },
        {
          title: 'Progresso no Curso',
          key: 'progresso',
          render: (_, record: MatriculaListItem) => <ProgressoCell record={record} />,
        },
        {
          title: 'Última atividade',
          key: 'ultimaAtividade',
          render: (_, record: MatriculaListItem) => (
            <Text type="secondary" className="whitespace-nowrap">
              {formatUltimaAtividade(ultimaAtividade(record.estatisticasEstudos))}
            </Text>
          ),
        },
        {
          title: 'Data de matrícula',
          key: 'createdAt',
          render: (_, record: MatriculaListItem) => (
            <div className="whitespace-nowrap">
              <Text type="secondary">{formatDate(record.createdAt)}</Text>
              {record.pagamento.dataPagamento && (
                <div className="text-xs text-gray-400">
                  Pago em {formatDate(record.pagamento.dataPagamento)}
                </div>
              )}
            </div>
          ),
        },
        {
          title: '',
          key: 'acoes',
          fixed: 'right',
          render: (_, record: MatriculaListItem) => (
            <Button
              type="link"
              className="!font-semibold"
              onClick={() => navigate(`/detalhes/${record.id}`)}
            >
              Detalhes
            </Button>
          ),
        },
      ]}
      pagination={{
        total: totalRecords,
        current: filters.pageNumber,
        pageSize: filters.limit,
        onChange: irParaPagina,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50'],
        showTotal: (total) => `Total: ${total} alunos`,
      }}
    />
  )
}

function AlunosCards() {
  const data = useAlunosStore((s) => s.data)
  const loading = useAlunosStore((s) => s.loading)

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spin />
      </div>
    )
  }

  if (!data.length) {
    return <Empty description="Nenhum aluno matriculado ainda" className="py-12" />
  }

  return (
    <div className="flex flex-col gap-3">
      {data.map((matricula) => (
        <AlunoCard key={matricula.id} record={matricula} />
      ))}
      <PaginacaoDosCards />
    </div>
  )
}

function PaginacaoDosCards() {
  const totalRecords = useAlunosStore((s) => s.totalRecords)
  const filters = useAlunosStore((s) => s.filters)

  return (
    <Pagination
      simple
      align="center"
      className="py-2"
      total={totalRecords}
      current={filters.pageNumber}
      pageSize={filters.limit}
      onChange={irParaPagina}
      showTotal={(total) => `${total} alunos`}
    />
  )
}

function AlunoCard({ record }: { record: MatriculaListItem }) {
  const navigate = useNavigate()
  const { pagamento } = record
  const isPago = pagamento.dataPagamento !== null
  const progresso = calculateProgress(record.estatisticasEstudos, record.curso.modulos.length)

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center gap-3">
        <IniciaisDoAluno nome={record.aluno.nome} />
        <div className="min-w-0">
          <div className="font-bold text-[#1a3a5c] uppercase leading-snug break-words">
            {record.aluno.nome}
          </div>
          <Text type="secondary" className="text-xs">
            {formatCpf(record.aluno.cpf)}
          </Text>
        </div>
      </div>

      <div className="border-t border-gray-100 mt-4 pt-3 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium">Curso</div>
          <div className="font-bold text-[#1a3a5c]">
            {record.curso.nomeReduzido || record.curso.nome}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium">Valor</div>
          <ValorDoPagamento record={record} />
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between gap-3">
        <Tag color={isPago ? 'green' : 'red'} className="!mr-0">
          {isPago ? 'Pago' : 'Pendente'}
        </Tag>
        {isPago && (
          <Text type="secondary" className="text-xs">
            Pago em: {formatDateTime(pagamento.dataPagamento!)}
          </Text>
        )}
      </div>

      <div className="border-t border-gray-100 mt-4 pt-3 flex items-center justify-between gap-3">
        <TagDeStatus statusDetran={record.statusDetran} />
        <Text type="secondary" className="text-xs">
          Matrícula: {formatDateTime(record.createdAt)}
        </Text>
      </div>

      <div className="mt-3">
        <BarraDeProgresso progresso={progresso} />
      </div>
      <div className="mt-1 flex items-center justify-between">
        <Text type="secondary" className="text-xs">
          {faseDoCurso(record.estatisticasEstudos, progresso)} ·{' '}
          {formatUltimaAtividade(ultimaAtividade(record.estatisticasEstudos))}
        </Text>
      </div>

      <div className="mt-3 text-right">
        <Button
          type="link"
          className="!font-semibold !px-0"
          onClick={() => navigate(`/detalhes/${record.id}`)}
        >
          Detalhes <ArrowRightOutlined />
        </Button>
      </div>
    </div>
  )
}

function AlunoCell({ record }: { record: MatriculaListItem }) {
  function handleCopy() {
    copyToClipboard(stripCpf(record.aluno.cpf))
    toast.success('CPF copiado!')
  }

  return (
    <div className="min-w-[200px]">
      <div className="font-bold text-[#1a3a5c] uppercase leading-snug">{record.aluno.nome}</div>
      <div className="flex items-center gap-1.5">
        <Text type="secondary" className="text-xs">
          {formatCpf(record.aluno.cpf)}
        </Text>
        <Tooltip title="Copiar CPF">
          <CopyOutlined className="text-xs text-[#ff6422] cursor-pointer" onClick={handleCopy} />
        </Tooltip>
      </div>
    </div>
  )
}

function PagamentoCell({ record }: { record: MatriculaListItem }) {
  const { pagamento } = record
  const isPago = pagamento.dataPagamento !== null

  return (
    <div className="min-w-[110px]">
      <Tag color={isPago ? 'green' : 'red'} className="!mr-0">
        {isPago ? 'Pago' : 'Pendente'}
      </Tag>
      <div
        className="mt-1"
        title={isPago ? `Pago em ${formatDateTime(pagamento.dataPagamento!)}` : undefined}
      >
        <ValorDoPagamento record={record} />
      </div>
    </div>
  )
}

/** Sem repasse a matrícula foi vendida a preço de custo, e a linha verde só faria ruído. */
function ValorDoPagamento({ record }: { record: MatriculaListItem }) {
  const repasse = record.repasseDoParceiro

  return (
    <div>
      <div className="font-bold text-[#1a3a5c]">{formatCurrency(record.pagamento.valor)}</div>
      {repasse > 0 && (
        <div className="text-xs font-medium text-green-600">
          Seu repasse: {formatCurrency(repasse)}
        </div>
      )}
    </div>
  )
}

function ProgressoCell({ record }: { record: MatriculaListItem }) {
  const progresso = calculateProgress(record.estatisticasEstudos, record.curso.modulos.length)

  return (
    <div className="min-w-[160px]">
      <Text type="secondary" className="text-xs">
        {faseDoCurso(record.estatisticasEstudos, progresso)}
      </Text>
      <BarraDeProgresso progresso={progresso} larguraDaBarra="110px" />
      <TagDeEsperaDoDetran record={record} />
    </div>
  )
}

/** O aluno terminou os módulos, mas o DETRAN só aceita a conclusão depois do prazo do curso. */
function TagDeEsperaDoDetran({ record }: { record: MatriculaListItem }) {
  if (record.statusDetran === 'FINALIZADO') return null
  if (!concluiuOsModulos(record.estatisticasEstudos, record.curso.modulos.length)) return null

  const dias = diasAteConclusaoNoDetran(record)

  return (
    <Tag color="gold" className="!mr-0 mt-1">
      {dias
        ? `Aguardando DETRAN · faltam ${dias} ${dias === 1 ? 'dia' : 'dias'}`
        : 'Aguardando DETRAN'}
    </Tag>
  )
}

function BarraDeProgresso({
  progresso,
  larguraDaBarra = '100%',
}: {
  progresso: number
  larguraDaBarra?: string
}) {
  const concluido = progresso === 100

  return (
    <div className="flex items-center gap-2">
      <div
        className="h-2 rounded-full bg-gray-200 overflow-hidden"
        style={{ width: larguraDaBarra, flex: larguraDaBarra === '100%' ? 1 : undefined }}
      >
        <div
          className="h-full rounded-full"
          style={{ width: `${progresso}%`, backgroundColor: concluido ? '#10b981' : '#1a3a5c' }}
        />
      </div>
      <span className={`text-xs font-semibold ${concluido ? 'text-green-600' : 'text-[#1a3a5c]'}`}>
        {progresso}%
      </span>
    </div>
  )
}

// ─── Utils ────────────────────────────────────────────────────────────────────

function jaOfertado(curso: Curso): boolean {
  return !!curso.ofertadoDesde && new Date(curso.ofertadoDesde) <= new Date()
}

function AggregatedPixBar() {
  const selectedIds = useAlunosStore((s) => s.selectedIds)
  const data = useAlunosStore((s) => s.data)
  const clearSelection = useAlunosStore((s) => s.clearSelection)
  const gerarCobrancaAgregada = useAlunosStore((s) => s.gerarCobrancaAgregada)
  const cobrancaAgregadaLoading = useAlunosStore((s) => s.cobrancaAgregadaLoading)

  if (selectedIds.length === 0) return null

  const selectedItems = data.filter((item) => selectedIds.includes(item.id))
  const totalValorNewDriver = selectedItems.reduce(
    (sum, item) => sum + (item.pagamento.valorNewDriver ?? 0),
    0
  )

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg px-6 py-4">
      <div className="max-w-screen-xl mx-auto flex items-center justify-between gap-6">
        <div className="flex items-center gap-8">
          <div className="flex flex-col">
            <Text type="secondary" className="text-xs">
              Selecionados
            </Text>
            <Text strong className="text-lg">
              {selectedIds.length} aluno{selectedIds.length !== 1 ? 's' : ''}
            </Text>
          </div>
          <div className="flex flex-col">
            <Text type="secondary" className="text-xs">
              Total a pagar ao New Driver
            </Text>
            <Text strong className="text-lg">
              {formatCurrency(totalValorNewDriver)}
            </Text>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button size="large" onClick={clearSelection}>
            Cancelar seleção
          </Button>
          <Button
            type="primary"
            size="large"
            loading={cobrancaAgregadaLoading}
            onClick={gerarCobrancaAgregada}
          >
            Gerar PIX agregado
          </Button>
        </div>
      </div>
    </div>
  )
}

function CobrancaAgregadaModal() {
  const cobrancaAgregada = useAlunosStore((s) => s.cobrancaAgregada)
  const setCobrancaAgregada = useAlunosStore((s) => s.setCobrancaAgregada)
  const clearSelection = useAlunosStore((s) => s.clearSelection)
  const fetchAlunos = useAlunosStore((s) => s.fetchAlunos)
  const [paid, setPaid] = useState(false)

  useEffect(() => {
    if (!cobrancaAgregada) return
    setPaid(false)
    const interval = setInterval(async () => {
      const { status } = await getCobrancaAgregada(cobrancaAgregada.id)
      if (status === 'PAGO') {
        clearInterval(interval)
        setPaid(true)
        toast.success('Pagamento confirmado!')
        clearSelection()
        setCobrancaAgregada(null)
        await fetchAlunos()
      }
    }, 3000)
    return () => clearInterval(interval)
  }, [cobrancaAgregada])

  function handleCancel() {
    setCobrancaAgregada(null)
    setPaid(false)
  }

  return (
    <Modal
      open={!!cobrancaAgregada}
      title="Pagar via PIX"
      closable={false}
      maskClosable={false}
      footer={null}
    >
      {cobrancaAgregada && (
        <div className="flex flex-col items-center gap-4 py-4">
          <QRCode value={cobrancaAgregada.pix} size={200} />
          <PixCopyableRow pix={cobrancaAgregada.pix} />
          <PixPollingStatus paid={paid} />
          <Button onClick={handleCancel}>Cancelar</Button>
        </div>
      )}
    </Modal>
  )
}

function PixCopyableRow({ pix }: { pix: string }) {
  function handleCopy() {
    copyToClipboard(pix)
    toast.success('Copiado!')
  }

  return (
    <div className="flex items-center gap-2 w-full">
      <Text strong className="shrink-0">
        Copia e cola:
      </Text>
      <Input value={pix} readOnly size="small" className="flex-1" />
      <Button icon={<CopyOutlined />} onClick={handleCopy} size="small">
        Copiar
      </Button>
    </div>
  )
}

function PixPollingStatus({ paid }: { paid: boolean }) {
  if (paid) {
    return <Alert type="success" message="Pagamento confirmado!" showIcon />
  }
  return (
    <div className="flex items-center gap-2 text-gray-500">
      <Spin size="small" />
      <Text type="secondary">Aguardando pagamento...</Text>
    </div>
  )
}
